import React from "react"
import { BridgeSdkProvider } from "@usebridge/sdk-react"
import { useInsuranceCheck } from "../insuranceEligibility"
import type { InsuranceMember, InsuranceInputs, InsuranceEvent } from "../insuranceTypes"
import { BookingFlowError } from "./BookingFlowError"
import type { InsuranceBridgeConfig } from "./insurance/types"
export type { InsuranceBridgeConfig } from "./insurance/types"

type CheckActions = {
  submit: (inputs: InsuranceInputs) => Promise<void>
  back: () => void
  memberReady: boolean
}
type Props = {
  bridgeConfig?: InsuranceBridgeConfig
  member?: InsuranceMember
  serviceTypeIds: string[]
  onInsuranceEvent: (event: InsuranceEvent) => void
  onBack: () => void
  onExit?: () => void
  children: (actions: CheckActions) => React.ReactNode
}

// The parent owns the layout; this boundary keeps the Bridge session mounted between steps.
export function InsuranceCheckPage(props: Props): JSX.Element {
  const config = props.bridgeConfig
  if (!config?.publishableKey.trim() || !["sandbox", "production"].includes(config.environment)) {
    return <BookingFlowError onExit={props.onExit} exitLabel="Back to Care Team" />
  }
  return (
    <BridgeSdkProvider key={`${config.environment}:${config.publishableKey}`} config={config} prefetchPayers={false}>
      <ConnectedInsuranceCheck {...props} />
    </BridgeSdkProvider>
  )
}

function ConnectedInsuranceCheck({ member, serviceTypeIds, onInsuranceEvent, onBack, children }: Props): JSX.Element {
  const check = useInsuranceCheck(member, serviceTypeIds, onInsuranceEvent)
  const back = () => {
    check.invalidate()
    onBack()
  }
  return <>{children({ submit: check.submit, back, memberReady: check.memberReady && serviceTypeIds.length > 0 })}</>
}
